<script>
import { ArtworkIntakeUploader, FILE_STATE } from "../services/ArtworkIntakeUploader.js";

const KB = 1024;

const DATA_TYPE = {
  TEXT: "Single Line Text",
  DATE: "Datepicker",
  DROPDOWN: "Dropdown Select",
  CHECKBOXES: "Checkboxes (Multiple Options)",
};

function readEmbedToken() {
  const hash = (window.location.hash || "").replace(/^#/, "");
  if (!hash) return "";
  const params = new URLSearchParams(hash);
  return params.get("token") || "";
}

// External references from the page URL's query string (e.g. ?zohoJobId=123 on a
// pre-linked form; the embed loader forwards the host page's query string here).
// Sent with the submission and echoed in webhooks. The server re-sanitizes.
function readExternalMetaData() {
  const meta = {};
  try {
    const params = new URLSearchParams(window.location.search || "");
    let count = 0;
    for (const [key, value] of params.entries()) {
      if (!key || key === "token" || !value || count >= 10) continue;
      meta[key] = value;
      count++;
    }
  } catch (e) {
    /* no metadata */
  }
  return Object.keys(meta).length > 0 ? meta : undefined;
}

function emptyValueFor(field) {
  return field.dataType === DATA_TYPE.CHECKBOXES ? [] : "";
}

export default {
  name: "ArtworkIntakeFormPublic",
  data() {
    return {
      isLoading: true,
      config: null,
      notFound: false,
      tokenMissing: false,
      embedToken: "",
      isSubmitting: false,
      submitted: false,
      isDragging: false,
      form: {
        name: "",
        email: "",
        phone: "",
        customFieldValues: {},
        files: [],
      },
      uploadItems: [],
      errors: {},
      FILE_STATE,
      DATA_TYPE,
    };
  },
  computed: {
    formId() {
      return this.$route.params.formId;
    },
    totalSize() {
      return this.form.files.reduce((a, f) => a + (f.size || 0), 0);
    },
  },
  methods: {
    formatSize(bytes) {
      if (bytes == null) return "";
      if (bytes < KB) return `${bytes} B`;
      if (bytes < KB * KB) return `${(bytes / KB).toFixed(1)} KB`;
      return `${(bytes / (KB * KB)).toFixed(2)} MB`;
    },
    onFileChange(e) {
      this.onFilesAdded(Array.from(e.target.files || []));
      e.target.value = "";
    },
    onDragOver(e) {
      e.preventDefault();
      if (!this.isSubmitting) this.isDragging = true;
    },
    onDragLeave(e) {
      e.preventDefault();
      this.isDragging = false;
    },
    onDrop(e) {
      e.preventDefault();
      this.isDragging = false;
      if (this.isSubmitting) return;
      this.onFilesAdded(Array.from(e.dataTransfer.files || []));
    },
    removeFile(index) {
      this.form.files.splice(index, 1);
      if (this.uploadItems.length) this.uploadItems.splice(index, 1);
      if (this.errors.submit) this.$delete(this.errors, "submit");
    },
    onFilesAdded(incoming) {
      this.form.files = [...this.form.files, ...incoming];
      if (this.errors.files) this.$delete(this.errors, "files");
    },
    toggleCheckbox(fieldId, option) {
      const current = this.form.customFieldValues[fieldId] || [];
      const idx = current.indexOf(option);
      const next = idx >= 0
        ? current.filter((v) => v !== option)
        : [...current, option];
      this.$set(this.form.customFieldValues, fieldId, next);
    },
    fieldValueAsArray(fieldId) {
      const v = this.form.customFieldValues[fieldId];
      if (Array.isArray(v)) return v.filter((s) => s !== "");
      if (v == null || v === "") return [];
      return [String(v)];
    },
    validate() {
      const errors = {};
      if (!this.form.name.trim()) errors.name = "Name is required";
      if (!this.form.email.trim()) errors.email = "Email is required";
      else if (!/^\S+@\S+\.\S+$/.test(this.form.email)) errors.email = "Enter a valid email";
      (this.config.customFields || []).forEach((f) => {
        if (f.required && this.fieldValueAsArray(f.accountProofPropertyId).length === 0) {
          errors[f.accountProofPropertyId] = `${f.label} is required`;
        }
      });
      if (this.config.requireFile && !this.form.files.length) {
        errors.files = "At least one file is required";
      }
      this.errors = errors;
      return Object.keys(errors).length === 0;
    },
    async submit() {
      if (!this.validate()) return;
      this.isSubmitting = true;
      try {
        let fileRefs = [];
        const uploader = new ArtworkIntakeUploader({
          formId: this.formId,
          embedToken: this.embedToken,
          service: this.$A.ArtworkIntakeService,
          files: this.form.files,
          onFileChange: (items) => {
            this.uploadItems = items;
          },
        });
        if (this.form.files.length) {
          fileRefs = await uploader.uploadAll();
        }

        const customFieldValues = (this.config.customFields || []).map((f) => ({
          accountProofPropertyId: f.accountProofPropertyId,
          value: this.fieldValueAsArray(f.accountProofPropertyId),
        }));

        const result = await this.$A.ArtworkIntakeService.submitForm(
          this.formId,
          {
            draftSubmissionId: uploader.draftSubmissionId,
            submitterName: this.form.name,
            submitterEmail: this.form.email,
            submitterPhone: this.form.phone,
            externalMetaData: readExternalMetaData(),
            customFieldValues,
            files: fileRefs,
          },
          this.embedToken
        );
        this.submitted = true;
        this.postToParent({
          type: "ashore-intake:submitted",
          submissionId: result && result.submissionId,
        });
      } catch (e) {
        console.error("Submit failed:", e);
        const fileLabel = e && e.file ? `${e.file}: ` : "";
        const message =
          (e && e.response && e.response.data && e.response.data.message) ||
          (e && e.message) ||
          "Something went wrong. Please try again.";
        const firstLine = String(message).split("\n", 1)[0].replace(/^.*Exception:\s*/, "");
        this.errors.submit = fileLabel + firstLine;
      }
      this.isSubmitting = false;
    },
    postToParent(message) {
      if (window.parent && window.parent !== window) {
        window.parent.postMessage(message, "*");
      }
    },
    postHeight() {
      const el = this.$refs.root;
      if (!el) return;
      const height = Math.ceil(el.getBoundingClientRect().height);
      this.postToParent({ type: "ashore-intake:resize", height });
    },
  },
  async mounted() {
    this.embedToken = readEmbedToken();
    if (!this.embedToken) {
      try {
        this.embedToken = await this.$A.ArtworkIntakeService.fetchEmbedToken(this.formId);
      } catch (e) {
        console.error("Failed to fetch embed token:", e);
      }
    }
    if (!this.embedToken) {
      this.notFound = true;
      this.isLoading = false;
      return;
    }
    try {
      this.config = await this.$A.ArtworkIntakeService.getPublicFormConfig(this.formId, this.embedToken);
      if (!this.config) {
        this.notFound = true;
      } else {
        (this.config.customFields || []).forEach((f) => {
          this.$set(this.form.customFieldValues, f.accountProofPropertyId, emptyValueFor(f));
        });
      }
    } catch (e) {
      console.error("Failed to load form config:", e);
      this.notFound = true;
    }
    this.isLoading = false;

    this.$nextTick(() => {
      this.postHeight();
      if (typeof ResizeObserver !== "undefined" && this.$refs.root) {
        this._resizeObserver = new ResizeObserver(() => this.postHeight());
        this._resizeObserver.observe(this.$refs.root);
      }
    });
  },
  beforeDestroy() {
    if (this._resizeObserver) {
      this._resizeObserver.disconnect();
      this._resizeObserver = null;
    }
  },
};
</script>

<template>
  <div class="aip-page" ref="root">
    <div class="aip-shell">
      <div v-if="isLoading" class="aip-state">
        <div class="aip-spinner" aria-hidden="true"></div>
        <p class="aip-state-text">Preparing your intake form…</p>
      </div>

      <div v-else-if="tokenMissing" class="aip-card aip-state-card">
        <div class="aip-state-mark aip-state-mark--warn" aria-hidden="true">!</div>
        <p class="aip-eyebrow">Restricted access</p>
        <h1 class="aip-display">This form can't be loaded directly</h1>
        <p class="aip-state-body">
          Use the embed snippet on the printer's website, or open the secure link they provided.
          If you reached this page from a valid link, try refreshing.
        </p>
      </div>

      <div v-else-if="notFound" class="aip-card aip-state-card">
        <div class="aip-state-mark aip-state-mark--muted" aria-hidden="true">×</div>
        <p class="aip-eyebrow">Not available</p>
        <h1 class="aip-display">Form not found</h1>
        <p class="aip-state-body">This intake form is no longer accepting submissions.</p>
      </div>

      <div v-else-if="submitted" class="aip-card aip-state-card">
        <div class="aip-state-mark aip-state-mark--success" aria-hidden="true">
          <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="4 12 10 18 20 6" />
          </svg>
        </div>
        <p class="aip-eyebrow">Submission received</p>
        <h1 class="aip-display">Thank you, {{ form.name }}.</h1>
        <p class="aip-state-body">
          Your artwork has been delivered to <strong>{{ config.tenantName }}</strong>.
          They'll review the files and follow up by email shortly.
        </p>
      </div>

      <div v-else class="aip-card">
        <header class="aip-header">
          <div class="aip-logo-frame">
            <img
              :src="config.tenantLogoUrl || '/img/ashore-logo.svg'"
              :alt="config.tenantName"
              class="aip-logo"
            />
          </div>
          <p class="aip-eyebrow">{{ config.tenantName }}</p>
          <h1 class="aip-display">Artwork Intake</h1>
          <p v-if="config.description" class="aip-lede">{{ config.description }}</p>
        </header>

        <form @submit.prevent="submit" class="aip-form" novalidate>
          <section class="aip-section">
            <p class="aip-section-rule"><span>01 · Your details</span></p>

            <div class="aip-grid">
              <div class="aip-field">
                <label for="aip-name">Your name</label>
                <input
                  id="aip-name"
                  type="text"
                  autocomplete="name"
                  v-model="form.name"
                  :class="{ 'is-error': errors.name }"
                  placeholder="Jane Doe"
                />
                <div v-if="errors.name" class="aip-error">{{ errors.name }}</div>
              </div>

              <div class="aip-field">
                <label for="aip-email">Email</label>
                <input
                  id="aip-email"
                  type="email"
                  autocomplete="email"
                  inputmode="email"
                  v-model="form.email"
                  :class="{ 'is-error': errors.email }"
                  placeholder="you@studio.com"
                />
                <div v-if="errors.email" class="aip-error">{{ errors.email }}</div>
              </div>

              <div class="aip-field">
                <label for="aip-phone">Phone <span class="aip-optional">(optional)</span></label>
                <input
                  id="aip-phone"
                  type="tel"
                  autocomplete="tel"
                  inputmode="tel"
                  v-model="form.phone"
                  placeholder="(555) 123-4567"
                />
              </div>
            </div>
          </section>

          <section v-if="config.customFields && config.customFields.length" class="aip-section">
            <p class="aip-section-rule"><span>02 · Job details</span></p>

            <div class="aip-stack">
              <div class="aip-field" v-for="cf in config.customFields" :key="cf.accountProofPropertyId">
                <label :for="'aip-cf-' + cf.accountProofPropertyId">
                  {{ cf.label }}<span v-if="cf.required" class="aip-req" aria-hidden="true">*</span>
                </label>

                <select
                  v-if="cf.dataType === DATA_TYPE.DROPDOWN"
                  :id="'aip-cf-' + cf.accountProofPropertyId"
                  v-model="form.customFieldValues[cf.accountProofPropertyId]"
                  :class="{ 'is-error': errors[cf.accountProofPropertyId] }"
                >
                  <option value="">Select…</option>
                  <option v-for="v in cf.enumValues" :key="v" :value="v">{{ v }}</option>
                </select>

                <input
                  v-else-if="cf.dataType === DATA_TYPE.DATE"
                  type="date"
                  :id="'aip-cf-' + cf.accountProofPropertyId"
                  v-model="form.customFieldValues[cf.accountProofPropertyId]"
                  :class="{ 'is-error': errors[cf.accountProofPropertyId] }"
                />

                <div v-else-if="cf.dataType === DATA_TYPE.CHECKBOXES" class="aip-checks">
                  <label
                    v-for="opt in cf.enumValues"
                    :key="opt"
                    class="aip-check"
                    :class="{ 'is-checked': (form.customFieldValues[cf.accountProofPropertyId] || []).includes(opt) }"
                  >
                    <input
                      type="checkbox"
                      :checked="(form.customFieldValues[cf.accountProofPropertyId] || []).includes(opt)"
                      @change="toggleCheckbox(cf.accountProofPropertyId, opt)"
                    />
                    <span class="aip-check-box" aria-hidden="true"></span>
                    <span class="aip-check-label">{{ opt }}</span>
                  </label>
                </div>

                <input
                  v-else
                  type="text"
                  :id="'aip-cf-' + cf.accountProofPropertyId"
                  v-model="form.customFieldValues[cf.accountProofPropertyId]"
                  :class="{ 'is-error': errors[cf.accountProofPropertyId] }"
                />

                <div v-if="errors[cf.accountProofPropertyId]" class="aip-error">
                  {{ errors[cf.accountProofPropertyId] }}
                </div>
              </div>
            </div>
          </section>

          <section class="aip-section">
            <p class="aip-section-rule"><span>{{ (config.customFields && config.customFields.length) ? "03" : "02" }} · Artwork files<span v-if="config.requireFile" class="aip-req" aria-hidden="true">*</span></span></p>

            <label
              class="aip-drop"
              :class="{ 'is-dragging': isDragging, 'is-disabled': isSubmitting, 'is-error': errors.files }"
              @dragover="onDragOver"
              @dragleave="onDragLeave"
              @drop="onDrop"
            >
              <input
                type="file"
                multiple
                :disabled="isSubmitting"
                @change="onFileChange"
                class="aip-drop-input"
              />
              <div class="aip-drop-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M12 3v12" />
                  <polyline points="7 8 12 3 17 8" />
                  <path d="M5 21h14" />
                </svg>
              </div>
              <div class="aip-drop-text">
                <strong>Drop files here</strong>
                <span>or tap to browse — PDFs, images, design source files</span>
              </div>
            </label>

            <ul v-if="form.files.length" class="aip-files">
              <li
                v-for="(file, i) in form.files"
                :key="file.name + i + '_' + file.size"
                class="aip-file"
                :class="uploadItems[i] && uploadItems[i].state ? 'state-' + uploadItems[i].state : ''"
              >
                <div class="aip-file-row">
                  <div class="aip-file-icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                      <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
                      <polyline points="14 3 14 8 19 8" />
                    </svg>
                  </div>
                  <div class="aip-file-meta">
                    <span class="aip-file-name">{{ file.name }}</span>
                    <span class="aip-file-size">{{ formatSize(file.size) }}</span>
                  </div>

                  <template v-if="isSubmitting && uploadItems[i]">
                    <span class="aip-file-status" :class="'state-' + uploadItems[i].state">
                      <template v-if="uploadItems[i].state === FILE_STATE.UPLOADING">{{ uploadItems[i].progress }}%</template>
                      <template v-else-if="uploadItems[i].state === FILE_STATE.DONE">Sent</template>
                      <template v-else-if="uploadItems[i].state === FILE_STATE.FAILED">Failed</template>
                      <template v-else>Queued</template>
                    </span>
                  </template>
                  <button
                    v-else
                    type="button"
                    class="aip-file-remove"
                    @click="removeFile(i)"
                    aria-label="Remove file"
                  >
                    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                      <line x1="6" y1="6" x2="18" y2="18" />
                      <line x1="6" y1="18" x2="18" y2="6" />
                    </svg>
                  </button>
                </div>

                <div
                  v-if="isSubmitting && uploadItems[i] && uploadItems[i].state === FILE_STATE.UPLOADING"
                  class="aip-progress"
                >
                  <div class="aip-progress-fill" :style="{ width: (uploadItems[i].progress || 0) + '%' }"></div>
                </div>

                <div
                  v-if="uploadItems[i] && uploadItems[i].state === FILE_STATE.FAILED && uploadItems[i].error"
                  class="aip-file-error"
                >
                  {{ uploadItems[i].error }}
                </div>
              </li>
            </ul>

            <p v-if="form.files.length" class="aip-files-summary">
              {{ form.files.length }} {{ form.files.length === 1 ? "file" : "files" }} · {{ formatSize(totalSize) }} total
            </p>
            <div v-if="errors.files" class="aip-error">{{ errors.files }}</div>
          </section>

          <div v-if="errors.submit" class="aip-error aip-error--block">{{ errors.submit }}</div>

          <button class="aip-submit" type="submit" :disabled="isSubmitting">
            <span v-if="isSubmitting" class="aip-submit-spinner" aria-hidden="true"></span>
            <span>{{ isSubmitting ? "Sending artwork…" : "Submit artwork" }}</span>
            <svg v-if="!isSubmitting" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <line x1="4" y1="12" x2="20" y2="12" />
              <polyline points="14 6 20 12 14 18" />
            </svg>
          </button>
        </form>
      </div>

      <p class="aip-footer">
        Secured intake by <a href="https://ashoreapp.com" target="_blank" rel="noopener noreferrer"><span>Ashore</span></a>
      </p>
    </div>
  </div>
</template>

<style scoped>
.aip-page {
  --paper: #f4f5f7;
  --paper-deep: #e9ebef;
  --ink: #111827;
  --ink-soft: #374151;
  --muted: #6b7280;
  --rule: #e5e7eb;
  --rule-soft: #eef0f3;
  --surface: #ffffff;
  --brand: #019256;
  --brand-deep: #017848;
  --brand-tint: #e7f3ec;
  --brand-glow: rgba(1, 146, 86, 0.18);
  --error: #b42318;
  --error-tint: #fdecea;

  min-height: 100vh;
  width: 100%;
  background: var(--paper);
  color: var(--ink);
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, Roboto, "Helvetica Neue", Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  padding: 56px 20px 80px;
  display: flex;
  justify-content: center;
}

.aip-shell {
  width: 100%;
  max-width: 640px;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 28px;
}

.aip-card {
  background: var(--surface);
  border: 1px solid var(--rule);
  border-radius: 18px;
  padding: 40px 40px 36px;
  box-shadow:
    0 1px 0 #fff inset,
    0 1px 2px rgba(26, 24, 21, 0.04),
    0 24px 48px -28px rgba(26, 24, 21, 0.18);
  position: relative;
}

.aip-card::before {
  content: "";
  position: absolute;
  inset: 6px;
  border: 1px solid var(--rule-soft);
  border-radius: 12px;
  pointer-events: none;
}

/* HEADER */
.aip-header {
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  padding-bottom: 8px;
  margin-bottom: 24px;
  position: relative;
}

.aip-logo-frame {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: var(--paper);
  border: 1px solid var(--rule);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 6px;
}

.aip-logo {
  max-width: 46px;
  max-height: 46px;
  object-fit: contain;
}

.aip-eyebrow {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.18em;
  color: var(--brand);
  margin: 0;
}

.aip-display {
  font-family: inherit;
  font-weight: 600;
  font-size: 26px;
  line-height: 1.2;
  letter-spacing: -0.01em;
  color: var(--ink);
  margin: 0;
}

.aip-lede {
  color: var(--ink-soft);
  font-size: 15px;
  line-height: 1.55;
  max-width: 36ch;
  margin: 0;
}

/* FORM */
.aip-form {
  display: flex;
  flex-direction: column;
  gap: 36px;
}

.aip-section {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.aip-section-rule {
  display: flex;
  align-items: center;
  gap: 14px;
  margin: 0;
  color: var(--muted);
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.16em;
}

.aip-section-rule::after {
  content: "";
  flex: 1;
  height: 1px;
  background: var(--rule);
}

.aip-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.aip-stack {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.aip-field {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.aip-field label {
  font-size: 12px;
  font-weight: 600;
  color: var(--ink);
  letter-spacing: 0.01em;
}

.aip-optional {
  font-weight: 400;
  color: var(--muted, #9aa0a6);
}

.aip-req {
  color: var(--error);
  margin-left: 4px;
  font-weight: 700;
}

.aip-field input[type="text"],
.aip-field input[type="email"],
.aip-field input[type="tel"],
.aip-field input[type="date"],
.aip-field select {
  width: 100%;
  padding: 12px 14px;
  border: 1px solid var(--rule);
  border-radius: 10px;
  background: var(--paper);
  color: var(--ink);
  font-family: inherit;
  font-size: 15px;
  font-weight: 500;
  transition: border-color 120ms ease, box-shadow 120ms ease, background 120ms ease;
  appearance: none;
  -webkit-appearance: none;
}

.aip-field select {
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8' fill='none'><path d='M1 1.5L6 6.5L11 1.5' stroke='%231a1815' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/></svg>");
  background-repeat: no-repeat;
  background-position: right 14px center;
  padding-right: 38px;
}

.aip-field input::placeholder { color: var(--muted); }

.aip-field input:focus,
.aip-field select:focus {
  outline: none;
  background: var(--surface);
  border-color: var(--brand);
  box-shadow: 0 0 0 4px var(--brand-glow);
}

.aip-field input.is-error,
.aip-field select.is-error {
  border-color: var(--error);
  background: var(--error-tint);
}

/* CHECKBOXES */
.aip-checks {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 2px;
}

.aip-check {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 11px 14px;
  border: 1px solid var(--rule);
  border-radius: 10px;
  background: var(--paper);
  cursor: pointer;
  transition: border-color 120ms ease, background 120ms ease;
  font-weight: 500;
}

.aip-check:hover {
  border-color: #d3cab8;
}

.aip-check.is-checked {
  background: var(--brand-tint);
  border-color: var(--brand);
}

.aip-check input {
  position: absolute;
  opacity: 0;
  pointer-events: none;
}

.aip-check-box {
  width: 18px;
  height: 18px;
  border-radius: 5px;
  border: 1.5px solid var(--rule);
  background: var(--surface);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 120ms ease;
  position: relative;
}

.aip-check.is-checked .aip-check-box {
  background: var(--brand);
  border-color: var(--brand);
}

.aip-check.is-checked .aip-check-box::after {
  content: "";
  width: 5px;
  height: 9px;
  border: solid #fff;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg) translate(-1px, -1px);
}

.aip-check-label {
  font-size: 14px;
  color: var(--ink);
}

/* ERRORS */
.aip-error {
  color: var(--error);
  font-size: 12px;
  font-weight: 500;
  margin-top: 2px;
}

.aip-error--block {
  padding: 12px 14px;
  background: var(--error-tint);
  border: 1px solid #f4cfca;
  border-radius: 8px;
  font-size: 13px;
}

/* DROP ZONE */
.aip-drop {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 32px 24px;
  border: 1.5px dashed var(--rule);
  border-radius: 14px;
  background: var(--paper);
  text-align: center;
  cursor: pointer;
  transition: all 160ms ease;
}

.aip-drop:hover {
  border-color: #cbd2da;
  background: var(--paper-deep);
}

.aip-drop.is-dragging {
  border-color: var(--brand);
  background: var(--brand-tint);
  transform: scale(1.005);
}

.aip-drop.is-disabled {
  cursor: not-allowed;
  opacity: 0.55;
}

.aip-drop.is-error {
  border-color: var(--error);
  background: var(--error-tint);
}

.aip-drop-input {
  position: absolute;
  inset: 0;
  opacity: 0;
  cursor: inherit;
}

.aip-drop-icon {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: var(--surface);
  border: 1px solid var(--rule);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--brand);
}

.aip-drop-text {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.aip-drop-text strong {
  font-size: 15px;
  font-weight: 600;
  color: var(--ink);
}

.aip-drop-text span {
  font-size: 13px;
  color: var(--muted);
}

/* FILE LIST */
.aip-files {
  list-style: none;
  margin: 16px 0 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.aip-file {
  border-top: 1px solid var(--rule-soft);
  padding: 12px 4px;
}

.aip-file:last-child {
  border-bottom: 1px solid var(--rule-soft);
}

.aip-file-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.aip-file-icon {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: var(--paper);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--ink-soft);
  flex-shrink: 0;
}

.aip-file-meta {
  display: flex;
  flex-direction: column;
  gap: 1px;
  flex: 1;
  min-width: 0;
}

.aip-file-name {
  font-size: 14px;
  font-weight: 500;
  color: var(--ink);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.aip-file-size {
  font-size: 11px;
  color: var(--muted);
  letter-spacing: 0.02em;
}

.aip-file-status {
  font-size: 11px;
  font-weight: 600;
  padding: 4px 9px;
  border-radius: 999px;
  background: var(--paper);
  color: var(--ink-soft);
  letter-spacing: 0.03em;
  text-transform: uppercase;
}

.aip-file-status.state-uploading { background: var(--brand-tint); color: var(--brand-deep); }
.aip-file-status.state-done { background: var(--brand); color: #fff; }
.aip-file-status.state-failed { background: var(--error-tint); color: var(--error); }

.aip-file-remove {
  width: 30px;
  height: 30px;
  border-radius: 8px;
  border: 1px solid transparent;
  background: transparent;
  color: var(--muted);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 120ms ease;
}

.aip-file-remove:hover {
  border-color: var(--rule);
  color: var(--error);
  background: var(--paper);
}

.aip-progress {
  margin-top: 10px;
  height: 3px;
  background: var(--rule-soft);
  border-radius: 999px;
  overflow: hidden;
}

.aip-progress-fill {
  height: 100%;
  background: var(--brand);
  transition: width 200ms ease;
}

.aip-file-error {
  margin-top: 8px;
  margin-left: 44px;
  padding: 6px 10px;
  background: var(--error-tint);
  border: 1px solid #f4cfca;
  border-radius: 6px;
  color: var(--error);
  font-size: 12px;
  line-height: 1.4;
}

.aip-files-summary {
  margin: 14px 0 0;
  font-size: 12px;
  color: var(--muted);
  letter-spacing: 0.02em;
}

/* SUBMIT */
.aip-submit {
  margin-top: 8px;
  width: 100%;
  padding: 16px 22px;
  background: var(--brand);
  color: #fff;
  border: 0;
  border-radius: 12px;
  font-family: inherit;
  font-size: 15px;
  font-weight: 600;
  letter-spacing: 0.005em;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  transition: background 140ms ease, transform 80ms ease, box-shadow 140ms ease;
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.18) inset,
    0 6px 18px -8px rgba(1, 146, 86, 0.5);
}

.aip-submit:hover:not(:disabled) {
  background: var(--brand-deep);
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.18) inset,
    0 10px 26px -10px rgba(1, 146, 86, 0.65);
}

.aip-submit:active:not(:disabled) {
  transform: translateY(1px);
}

.aip-submit:disabled {
  background: #b4b2ad;
  cursor: not-allowed;
  box-shadow: none;
}

.aip-submit-spinner {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.4);
  border-top-color: #fff;
  animation: aip-spin 0.7s linear infinite;
}

/* STATE CARDS */
.aip-state {
  text-align: center;
  padding: 80px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  color: var(--ink-soft);
}

.aip-spinner {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 2px solid var(--rule);
  border-top-color: var(--brand);
  animation: aip-spin 0.7s linear infinite;
}

.aip-state-text {
  font-size: 13px;
  color: var(--muted);
  letter-spacing: 0.02em;
}

.aip-state-card {
  text-align: center;
  padding: 56px 40px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
}

.aip-state-card .aip-display { max-width: 24ch; }
.aip-state-card .aip-state-body {
  color: var(--ink-soft);
  font-size: 15px;
  line-height: 1.55;
  max-width: 38ch;
  margin: 0;
}

.aip-state-mark {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: inherit;
  font-size: 26px;
  font-weight: 600;
  margin-bottom: 4px;
}

.aip-state-mark--success {
  background: var(--brand);
  color: #fff;
  box-shadow: 0 10px 24px -10px rgba(1, 146, 86, 0.5);
}
.aip-state-mark--warn {
  background: #fbf1d8;
  color: #8a6300;
  border: 1px solid #f0dfa8;
}
.aip-state-mark--muted {
  background: var(--paper);
  color: var(--muted);
  border: 1px solid var(--rule);
}

/* FOOTER */
.aip-footer {
  text-align: center;
  font-size: 11px;
  color: var(--muted);
  letter-spacing: 0.12em;
  text-transform: uppercase;
  margin: 0;
}

.aip-footer a {
  text-decoration: none;
  color: inherit;
}

.aip-footer span {
  color: var(--ink);
  font-weight: 600;
}

@keyframes aip-spin {
  to { transform: rotate(360deg); }
}

/* MOBILE */
@media (max-width: 600px) {
  .aip-page {
    padding: 24px 14px 60px;
  }
  .aip-card {
    padding: 28px 22px 26px;
    border-radius: 14px;
  }
  .aip-card::before {
    inset: 4px;
    border-radius: 10px;
  }
  .aip-display {
    font-size: 22px;
  }
  .aip-header {
    padding-bottom: 4px;
    margin-bottom: 20px;
  }
  .aip-form {
    gap: 28px;
  }
  .aip-grid {
    grid-template-columns: 1fr;
    gap: 18px;
  }
  .aip-drop {
    padding: 26px 18px;
  }
  .aip-file-row {
    gap: 10px;
  }
  .aip-file-icon {
    display: none;
  }
  .aip-submit {
    padding: 15px 18px;
  }
  .aip-state-card {
    padding: 44px 24px;
  }
}

@media (max-width: 380px) {
  .aip-display { font-size: 20px; }
  .aip-file-status { padding: 3px 7px; font-size: 10px; }
}
</style>
